import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useDisclosure,
  useToast,
  Text,
} from "@chakra-ui/react";
import EmployeeDataService from "../../services/Employee";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import {
  formatHrmsDateRange,
  LeaveTypeBadge,
  PersonCell,
} from "../../components/Hrms/hrmsUi";
import { getUserAccess } from "../../utils/authUtils";

interface PendingLeave {
  _id: string;
  name?: string;
  email: string;
  type: string;
  duration: string;
  start_date: string;
  end_date: string;
  reason: string;
  currentLevel?: number;
  requiredLevels?: number;
}

export const LeaveApprovals = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLeaves, setPendingLeaves] = useState<PendingLeave[]>([]);
  const [selectedLeave, setSelectedLeave] = useState<PendingLeave | null>(null);
  const [note, setNote] = useState("");

  const { canApproveLeaves } = getUserAccess();

  const fetchPendingApprovals = async () => {
    if (!canApproveLeaves) return;
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getPendingLeaveApprovals(
        token,
        userEmail,
      );
      if (res.data.status === "success") {
        setPendingLeaves(res.data.data || []);
      }
    } catch (e: any) {
      console.error("Error fetching pending approvals:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const openDecision = (leave: PendingLeave) => {
    setSelectedLeave(leave);
    setNote("");
    onOpen();
  };

  const handleDecision = async (action: "approved" | "declined") => {
    if (!selectedLeave) return;
    setIsSaving(true);
    try {
      const res = await EmployeeDataService.advanceLeaveApproval(
        token,
        selectedLeave._id,
        { action, note },
        userEmail,
      );
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: res.data.message,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        fetchPendingApprovals();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to record decision.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  return (
    <HrmsPageShell
      title="Leave Approvals"
      description={
        pendingLeaves.length
          ? `${pendingLeaves.length} request${pendingLeaves.length === 1 ? "" : "s"} waiting for your decision`
          : "Leave requests waiting for your decision appear here"
      }
      sectionKey="leave"
      noPermission={!canApproveLeaves}
      isLoading={canApproveLeaves && isLoading}
    >
      <HrmsTable
        head={["Employee", "Type", "Dates", "Reason", "Approval", ""]}
        isEmpty={pendingLeaves.length === 0}
        emptyText="No leave requests pending your approval"
      >
        {pendingLeaves.map((leave) => (
          <tr key={leave._id}>
            <td>
              <PersonCell name={leave.name || leave.email} subtitle={leave.email} />
            </td>
            <td>
              <LeaveTypeBadge type={leave.type} />
            </td>
            <td>
              <Text fontSize="13px" fontWeight="500" m={0} whiteSpace="nowrap">
                {formatHrmsDateRange(leave.start_date, leave.end_date)}
              </Text>
              <Text fontSize="11px" color="gray.500" m={0}>
                {leave.duration === "half" ? "Half day" : "Full day"}
              </Text>
            </td>
            <td>
              <Text fontSize="13px" color={leave.reason ? undefined : "gray.400"} m={0} noOfLines={2} maxW="260px">
                {leave.reason || "No reason given"}
              </Text>
            </td>
            <td>
              <Badge colorScheme="orange" borderRadius="full" px={2.5} fontSize="10px">
                {/* Leaves from before the approval workflow have no levels. */}
                {leave.requiredLevels
                  ? `Level ${leave.currentLevel || 1} of ${leave.requiredLevels}`
                  : "Awaiting decision"}
              </Badge>
            </td>
            <td style={{ textAlign: "right" }}>
              <Button
                size="sm"
                bg="#327171"
                color="white"
                _hover={{ bg: "#285e5e" }}
                onClick={() => openDecision(leave)}
              >
                Review
              </Button>
            </td>
          </tr>
        ))}
      </HrmsTable>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Review Leave — {selectedLeave?.name || selectedLeave?.email}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Note</FormLabel>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              mr={3}
              onClick={() => handleDecision("declined")}
              isLoading={isSaving}
            >
              Decline
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={() => handleDecision("approved")}
              isLoading={isSaving}
            >
              Approve
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
